import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  apiFetchRuntimeVerifications,
  apiRunRuntimeVerification,
} from '../studioApi.js'
import type { IntegrationHealth, StateSetter } from '../types'

export interface RuntimeVerification {
  id: string
  checkKey?: string
  status?: 'success' | 'failure' | 'pending'
  [key: string]: unknown
}

export interface RuntimeVerificationListResponse {
  runtimeVerifications?: RuntimeVerification[]
}

export interface RuntimeVerificationRunResponse {
  verification?: RuntimeVerification
}

export type RuntimeCheckInput =
  | string
  | { checkKey?: string; [k: string]: unknown }

export interface IntegrationHealthContextValue {
  health: IntegrationHealth
  verifications: RuntimeVerification[]
  loading: boolean
  error: Error | null
  refresh: () => Promise<
    RuntimeVerificationListResponse | RuntimeVerification[] | undefined
  >
  runCheck: (
    input?: RuntimeCheckInput
  ) => Promise<RuntimeVerificationRunResponse | undefined>
  setHealth: StateSetter<IntegrationHealth>
  setVerifications: StateSetter<RuntimeVerification[]>
}

const DEFAULT_HEALTH: IntegrationHealth = Object.freeze({
  ok: false,
  notionBriefs: false,
  flags: {},
  integrations: {},
}) as IntegrationHealth

const IntegrationHealthContext = createContext<IntegrationHealthContextValue>({
  health: DEFAULT_HEALTH,
  verifications: [],
  loading: false,
  error: null,
  refresh: async () => undefined,
  runCheck: async () => undefined,
  setHealth: () => {},
  setVerifications: () => {},
})

export interface IntegrationHealthProviderProps {
  children?: ReactNode
  initialHealth?: IntegrationHealth
  initialVerifications?: RuntimeVerification[]
  autoFetch?: boolean
}

export function IntegrationHealthProvider({
  children,
  initialHealth = DEFAULT_HEALTH,
  initialVerifications = [],
  autoFetch = true,
}: IntegrationHealthProviderProps) {
  const [health, setHealth] = useState<IntegrationHealth>(initialHealth)
  const [verifications, setVerifications] =
    useState<RuntimeVerification[]>(initialVerifications)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const next:
        | RuntimeVerificationListResponse
        | RuntimeVerification[]
        | undefined = await apiFetchRuntimeVerifications()
      if (
        next &&
        typeof next === 'object' &&
        !Array.isArray(next) &&
        next.runtimeVerifications
      ) {
        setVerifications(next.runtimeVerifications)
      } else if (Array.isArray(next)) {
        setVerifications(next)
      }
      return next
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const runCheck = useCallback(async (input?: RuntimeCheckInput) => {
    const payload =
      typeof input === 'string' ? { checkKey: input } : input || {}
    setLoading(true)
    setError(null)
    try {
      const result: RuntimeVerificationRunResponse =
        await apiRunRuntimeVerification(payload)
      if (result?.verification) {
        const fresh = result.verification
        setVerifications((prev) => [fresh, ...prev].slice(0, 200))
      }
      return result
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!autoFetch) return
    refresh().catch(() => {
      // surfaced via context.error
    })
  }, [autoFetch, refresh])

  const value = useMemo<IntegrationHealthContextValue>(
    () => ({
      health,
      verifications,
      loading,
      error,
      refresh,
      runCheck,
      setHealth,
      setVerifications,
    }),
    [health, verifications, loading, error, refresh, runCheck]
  )

  return (
    <IntegrationHealthContext.Provider value={value}>
      {children}
    </IntegrationHealthContext.Provider>
  )
}

export function useIntegrationHealth(): IntegrationHealthContextValue {
  return useContext(IntegrationHealthContext)
}

export { DEFAULT_HEALTH, IntegrationHealthContext }
